import React, { ElementType, PropsWithChildren, useMemo } from "react";
import BulletListItem from "./BulletListItem";
import ContentContext from "../Content/ContentContext";
import { TThemeVariant, useTheme } from "../../Utility/ThemeProvider";

export type TListType = "unordered" | "ordered" | "description";

export interface IBulletListProps<T = Record<string, unknown>> {
  /** Data to be passed into each ListItem */
  items: Array<T>;
  /** Special type of the list */
  listType?: TListType;
  /** Custom component for ol / ul.
   * All additional props for the List will be passed to custom component as well. */
  customListComponent?: ElementType;
  /** Custom component for a list item.
   * All data in `items` will be passed to this custom component as well. */
  customListItemComponent?: ElementType;
  /** Wrapper's main css class */
  className?: string;
  /** Disable all additional css classes */
  disableListClasses?: boolean;
  /** Override theme */
  variant?: TThemeVariant;
}

export const LIST_ORDERED_CLASSNAME = "uds-ordered-list";
export const LIST_UNORDERED_CLASSNAME = "uds-bullet-list";

function BulletList<T = Record<string, unknown>>({
  className,
  items,
  listType,
  customListComponent: CustomListComponent,
  customListItemComponent: CustomListItem,
  disableListClasses,
  variant,
  ...rest
}: PropsWithChildren<IBulletListProps<T>>) {
  const theme = useTheme(variant);

  const ListTag = useMemo(() => {
    if (CustomListComponent) return CustomListComponent;
    if (listType === "ordered") return "ol";
    return "ul";
  }, [CustomListComponent, listType]);

  const ItemTag = useMemo(
    () => CustomListItem || BulletListItem,
    [CustomListItem],
  );
  return (
    <ContentContext.Consumer>
      {({ isArticle }) => {
        const listClassName = (() => {
          if (!disableListClasses && !isArticle) {
            if (ListTag === "ol") return LIST_ORDERED_CLASSNAME;
            if (ListTag === "ul") return LIST_UNORDERED_CLASSNAME;
          }
          return "";
        })();
        let themeClassName = "";
        if (theme === "dark") {
          themeClassName = listClassName
            ? `${listClassName}--theme-dark`
            : "theme-dark";
        }
        return (
          <ListTag
            className={`${className || ""} ${listClassName} ${themeClassName}`}
            {...rest}
          >
            {items.map((item, index) => (
              <ItemTag
                key={(item as Record<string, unknown>)?.key ?? index}
                {...item}
              />
            ))}
          </ListTag>
        );
      }}
    </ContentContext.Consumer>
  );
}

export default BulletList;
